import React, {
  useRef,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  PatientAlertDTO,
  PatientDTO,
} from 'dtos';
import {
  AlertSeverity,
} from 'dtos/alertSeverity';
import {
  notEmptyString,
} from '@populateui/utils';
import Tooltip from 'core/components/Tooltip';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import AlertActions from 'views/EMR/PatientAlerts/AlertActions';

type Props = {
  alert: PatientAlertDTO;
  patientId: number;
  patient?: PatientCompact | Partial<PatientDTO> | null;
  encounterId?: number;
  className?: string;
  size?: 'small' | 'medium';
};

type AlertSeverityPresentation = {
  dotClassName: string;
  tagClassName: string;
};

type AlertCardSizeStyles = {
  actions: string;
  content: string;
  dot: string;
  meta: string;
  root: string;
  tag: string;
  text: string;
  title: string;
};

const ALERT_SEVERITY_PRESENTATION: Record<AlertSeverity, AlertSeverityPresentation> = {
  [AlertSeverity.Error]: {
    dotClassName: 'bg-red-400',
    tagClassName: 'bg-red-50 text-red-500',
  },
  [AlertSeverity.Warning]: {
    dotClassName: 'bg-amber-400',
    tagClassName: 'bg-amber-50 text-amber-600',
  },
  [AlertSeverity.Info]: {
    dotClassName: 'bg-blue-400',
    tagClassName: 'bg-blue-50 text-blue-500',
  },
  [AlertSeverity.Neutral]: {
    dotClassName: 'bg-slate-400',
    tagClassName: 'bg-slate-100 text-slate-600',
  },
};

const ALERT_CARD_SIZE_STYLES: Record<'small' | 'medium', AlertCardSizeStyles> = {
  medium: {
    actions: 'gap-2 pt-0.5',
    content: 'gap-0.5',
    dot: 'w-2 h-2 mt-1.5',
    meta: 'text-xs leading-5',
    root: 'gap-3 p-2',
    tag: 'px-2.5 py-0.5 text-xs',
    text: 'text-[13px] leading-6',
    title: 'text-sm',
  },
  small: {
    actions: 'gap-1.5 pt-0',
    content: 'gap-0',
    dot: 'w-2 h-2 mt-1',
    meta: 'text-[11px] leading-4',
    root: 'gap-2.5 px-2 py-1.5',
    tag: 'px-2 py-0 text-[11px]',
    text: 'text-xs leading-5',
    title: 'text-xs',
  },
};

type AutoTooltipSpanProps = {
  text: string;
  className?: string;
};

function AutoTooltipSpan({ text, className }: AutoTooltipSpanProps) {
  const ref = useRef<HTMLSpanElement>(null);
  return (
    <Tooltip title={(<div className="max-w-[400px]">{text}</div>)} auto ref={ref}>
      <span ref={ref} className={className}>{text}</span>
    </Tooltip>
  );
}

export default function AlertCard({
  alert,
  patientId,
  patient,
  encounterId,
  className,
  size = 'medium',
}: Props) {
  const {
    severity,
    label,
    text,
    appointment,
  } = alert;

  const alertSeverityPresentation = ALERT_SEVERITY_PRESENTATION[severity ?? AlertSeverity.Neutral]
    ?? ALERT_SEVERITY_PRESENTATION[AlertSeverity.Neutral];
  const sizeStyles = ALERT_CARD_SIZE_STYLES[size];

  return (
    <div
      className={clsx(
        'flex items-center border-b border-gray-200 last:border-b-0 last:rounded-b bg-white',
        sizeStyles.root,
        className,
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-1.5">
          <span
            className={clsx(
              'rounded-full shrink-0',
              sizeStyles.dot,
              alertSeverityPresentation.dotClassName,
            )}
          />
          <div className={clsx('flex-1 min-w-0 flex flex-col', sizeStyles.content)}>
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <p className={clsx('font-semibold text-gray-900 min-w-0', sizeStyles.title)}>
                {label}
              </p>
            </div>
            {notEmptyString(text) && (
              <p className={clsx('text-gray-600', sizeStyles.text)}>
                {text}
              </p>
            )}
            {isNil(encounterId) && notEmptyString(appointment?.displayName) && (
              <AutoTooltipSpan
                text={appointment?.displayName ?? ''}
                className={clsx('text-gray-500 truncate max-w-[70%]', sizeStyles.meta)}
              />
            )}
          </div>
        </div>
      </div>
      <AlertActions
        alert={alert}
        patientId={patientId}
        patient={patient}
        encounterId={encounterId}
        size={size}
      />
    </div>
  );
}
